<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisField from 'dashboard/components-next/LaisField.vue';
import LaisSelect from 'dashboard/components-next/LaisSelect.vue';
import {
  formatDateTime,
  statusClass,
  statusLabel,
} from '../utils/minhaLaisPresenters';

defineProps({
  whatsappMeta: { type: Object, required: true },
  whatsappQr: { type: Object, default: null },
  whatsappAgentOptions: { type: Array, default: () => [] },
  whatsappConnections: { type: Array, default: () => [] },
  activeWhatsappConnections: { type: Array, default: () => [] },
  isWhatsappPairingBusy: { type: Boolean, default: false },
  isGeneratingWhatsappQr: { type: Boolean, default: false },
  isLoadingWhatsappConnections: { type: Boolean, default: false },
  refreshingConnectionId: { type: [Number, String], default: null },
  updatingConnectionId: { type: [Number, String], default: null },
  deletingConnectionId: { type: [Number, String], default: null },
});

const emit = defineEmits([
  'generateQr',
  'renewQr',
  'changeOwner',
  'removeConnection',
]);

const whatsappSelectedOwnerId = defineModel('whatsappSelectedOwnerId', {
  type: [Number, String],
  default: null,
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <LaisCard title="Conexão QR">
    <div
      v-if="!whatsappMeta.whatsmeow_configured"
      class="mb-4 flex items-start gap-3 rounded-lais border border-[#f1c9cf] bg-[#fff5f6] p-4"
    >
      <span
        class="i-lucide-circle-alert mt-0.5 size-5 shrink-0 text-[#b42318]"
      />
      <p class="text-[13px] leading-relaxed text-[#912018]">
        A integração de WhatsApp não está configurada neste ambiente.
      </p>
    </div>

    <div class="grid gap-5 lg:grid-cols-[minmax(0,280px)_1fr]">
      <div class="rounded-lais border border-[#e5e7eb] bg-[#faf8fd] p-4">
        <div
          class="grid min-h-[240px] place-items-center rounded-lais border border-dashed border-[#d8d5de] bg-white p-4"
        >
          <img
            v-if="whatsappQr?.base64"
            :src="whatsappQr.base64"
            alt="QR code de conexão do WhatsApp"
            class="h-56 w-56 rounded-lais bg-white object-contain"
          />
          <div v-else class="text-center">
            <span
              class="i-lucide-qr-code mx-auto mb-3 block size-10 text-lais-primary"
            />
            <p class="text-[13px] font-semibold text-lais-text">
              Nenhum QR code gerado
            </p>
            <p class="mt-1 text-[12px] text-[#6b7280]">
              Gere um novo QR ou renove uma conexão existente.
            </p>
          </div>
        </div>

        <p
          v-if="whatsappQr?.pairing_code"
          class="mt-3 rounded-lais-sm bg-white px-3 py-2 font-mono text-[12px] text-lais-primary"
        >
          Código: {{ whatsappQr.pairing_code }}
        </p>
      </div>

      <div class="grid content-start gap-4">
        <div v-if="whatsappMeta.admin" class="grid gap-2">
          <LaisField label="Vincular nova conexão ao usuário">
            <LaisSelect
              v-model="whatsappSelectedOwnerId"
              :options="whatsappAgentOptions"
              placeholder="Escolha o responsável"
              searchable
            />
          </LaisField>
        </div>

        <button
          type="button"
          class="inline-flex h-11 items-center justify-center gap-2 rounded-lais bg-lais-primary px-4 text-[14px] font-semibold text-white transition-colors hover:bg-[#3a2762] disabled:cursor-not-allowed disabled:opacity-60"
          :disabled="
            isWhatsappPairingBusy || !whatsappMeta.whatsmeow_configured
          "
          @click="emit('generateQr')"
        >
          <span
            class="size-4"
            :class="
              isGeneratingWhatsappQr
                ? 'i-lucide-loader-circle animate-spin'
                : 'i-lucide-qr-code'
            "
            aria-hidden="true"
          />
          Gerar nova conexão
        </button>

        <div class="rounded-lais border border-[#e5e7eb] bg-white p-4">
          <p class="text-[13px] font-semibold text-lais-text">
            Conexões ativas
          </p>
          <p class="mt-1 text-[28px] font-bold text-lais-primary">
            {{ activeWhatsappConnections.length }}
          </p>
          <p class="text-[12px] text-[#6b7280]">
            Total listado: {{ whatsappConnections.length }}
          </p>
        </div>
      </div>
    </div>
  </LaisCard>

  <LaisCard title="Conexões vinculadas">
    <div
      v-if="isLoadingWhatsappConnections"
      class="flex items-center gap-2 rounded-lais border border-[#e5e7eb] bg-[#faf8fd] p-4 text-[13px] text-[#6b7280]"
    >
      <span class="i-lucide-loader-circle size-4 animate-spin" />
      Carregando conexões...
    </div>

    <div
      v-else-if="whatsappConnections.length === 0"
      class="rounded-lais border border-dashed border-[#d8d5de] bg-[#fcfbfd] px-4 py-8 text-center"
    >
      <span
        class="i-lucide-message-circle-off mx-auto mb-3 block size-8 text-lais-text-muted"
      />
      <p class="text-[13px] font-semibold text-lais-text">
        Nenhuma conexão encontrada
      </p>
      <p class="mt-1 text-[12px] text-[#6b7280]">
        Gere um QR code para conectar o primeiro WhatsApp.
      </p>
    </div>

    <div v-else class="grid gap-3">
      <article
        v-for="connection in whatsappConnections"
        :key="connection.id"
        class="rounded-lais border border-[#e5e7eb] bg-white p-4"
      >
        <div
          class="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between"
        >
          <div class="min-w-0">
            <div class="flex flex-wrap items-center gap-2">
              <h3 class="truncate text-[15px] font-semibold text-lais-text">
                {{ connection.profile_name || connection.instance_name }}
              </h3>
              <span
                class="inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold"
                :class="statusClass(connection.status)"
              >
                {{ statusLabel(connection.status) }}
              </span>
            </div>

            <p class="mt-1 text-[12px] text-[#6b7280]">
              {{
                connection.phone_number ||
                connection.owner_jid ||
                'Número ainda não identificado'
              }}
            </p>
            <p class="mt-1 text-[12px] text-[#8a8793]">
              Identificação: {{ connection.instance_name }}
            </p>
            <p class="mt-1 text-[12px] text-[#8a8793]">
              Última sincronização:
              {{ formatDateTime(connection.last_synced_at) }}
            </p>
          </div>

          <div class="grid gap-2 lg:min-w-[260px]">
            <LaisField v-if="whatsappMeta.admin" label="Responsável">
              <LaisSelect
                :model-value="connection.user?.id"
                :options="whatsappAgentOptions"
                placeholder="Sem responsável"
                searchable
                :disabled="updatingConnectionId === connection.id"
                @update:model-value="
                  value => emit('changeOwner', connection, value)
                "
              />
            </LaisField>
            <div v-else class="rounded-lais-sm bg-[#faf8fd] px-3 py-2">
              <p class="text-[11px] font-semibold uppercase text-[#8a8793]">
                Responsável
              </p>
              <p class="text-[13px] font-semibold text-lais-text">
                {{ connection.user?.name || 'Você' }}
              </p>
            </div>

            <div class="flex flex-wrap gap-2">
              <button
                v-if="!connection.active"
                type="button"
                class="inline-flex items-center gap-2 rounded-lais-sm border border-[#e5e7eb] bg-white px-3 py-2 text-[12px] font-semibold text-lais-primary transition-colors hover:border-lais-primary/50 disabled:cursor-not-allowed disabled:opacity-60"
                :disabled="
                  isWhatsappPairingBusy || !whatsappMeta.whatsmeow_configured
                "
                @click="emit('renewQr', connection)"
              >
                <span
                  class="size-3.5"
                  :class="
                    refreshingConnectionId === connection.id
                      ? 'i-lucide-loader-circle animate-spin'
                      : 'i-lucide-refresh-cw'
                  "
                />
                Renovar QR
              </button>

              <button
                v-if="whatsappMeta.admin"
                type="button"
                class="inline-flex items-center gap-2 rounded-lais-sm border border-[#f1c9cf] bg-white px-3 py-2 text-[12px] font-semibold text-[#b42318] transition-colors hover:bg-[#fff5f6] disabled:cursor-not-allowed disabled:opacity-60"
                :disabled="isWhatsappPairingBusy"
                @click="emit('removeConnection', connection)"
              >
                <span
                  class="size-3.5"
                  :class="
                    deletingConnectionId === connection.id
                      ? 'i-lucide-loader-circle animate-spin'
                      : 'i-lucide-trash-2'
                  "
                />
                Remover
              </button>
            </div>
          </div>
        </div>
      </article>
    </div>
  </LaisCard>
</template>
